import { LoginRequest, LoginResponse, JwtClaims, AuthState } from '../types/auth'
import { getApiBaseUrl } from '../api/config'
import { clearAllSearchRecents } from '../search/recents'

const TOKEN_KEY = 'nyos_jwt_token'
const USER_KEY = 'nyos_user_data'

export class AuthService {
  private static instance: AuthService

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService()
    }
    return AuthService.instance
  }

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await fetch(`${getApiBaseUrl()}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials)
    })

    if (!response.ok) {
      throw new Error('Login failed')
    }

    const data: LoginResponse = await response.json()
    this.setToken(data.token)
    this.setUser(data.username, data.role, data.facilityId, data.tenantId, data.operatingEntityId, data.availableFacilities)
    return data
  }

  logout(): void {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    // Scoped UX state must not leak across sessions (review requirement).
    clearAllSearchRecents()
    // Active yard selection is session state too — the next login must
    // land on their own primary facility, not the previous user's scope.
    localStorage.removeItem('nyos_active_scope')
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY)
  }

  private setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token)
  }

  getUser(): AuthState['user'] {
    const userData = localStorage.getItem(USER_KEY)
    if (!userData) return null
    return JSON.parse(userData)
  }

  private setUser(username: string, role: string, facilityId: string, tenantId?: string, operatingEntityId?: string, availableFacilities?: any[]): void {
    localStorage.setItem(USER_KEY, JSON.stringify({
      username,
      role,
      facilityId,
      tenantId,
      operatingEntityId,
      availableFacilities
    }))
  }

  isAuthenticated(): boolean {
    const token = this.getToken()
    if (!token) return false

    try {
      const claims = this.decodeToken(token)
      const now = Date.now() / 1000
      return claims.exp > now
    } catch {
      return false
    }
  }

  private decodeToken(token: string): JwtClaims {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  }

  getRole(): string | null {
    const user = this.getUser()
    return user?.role || null
  }
}

export const authService = AuthService.getInstance()
